import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../stores/auth'
import { updateOrganization, supabase } from '@unvld/db'
import { toast } from 'sonner'
import { Ticket, ArrowRight, CheckCircle, Building2, Palette, CreditCard, CalendarPlus, AlertTriangle, Loader2 } from 'lucide-react'
import { LogoUpload } from '../components/LogoUpload'

const STEPS = ['Club-Info', 'Branding', 'Stripe einrichten', 'Erstes Event']

const SUPABASE_URL = 'https://vvuhbraojktjsesoiljt.supabase.co/functions/v1'

export function OnboardingPage() {
  const [step, setStep] = useState(0)
  const { org, init } = useAuth()
  const navigate = useNavigate()

  // Step 0: Club-Info
  const [clubName, setClubName] = useState(org?.name || '')
  const [city, setCity] = useState((org as any)?.address?.city || '')
  const [description, setDescription] = useState((org as any)?.description || '')
  const [saving, setSaving] = useState(false)

  // Step 1: Branding
  const [primaryColor, setPrimaryColor] = useState((org as any)?.primary_color || '#6366f1')

  // Step 2: Stripe
  const [stripeLoading, setStripeLoading] = useState(false)

  const saveClubInfo = async () => {
    if (!org || !clubName.trim()) {
      toast.error('Bitte gib einen Club-Namen ein')
      return
    }
    setSaving(true)
    try {
      await updateOrganization(org.id, {
        name: clubName.trim(),
        address: { city: city.trim() },
        description: description.trim(),
      } as any)
      await init()
      toast.success('Club-Info gespeichert')
      setStep(1)
    } catch (err) {
      console.error(err)
      toast.error('Fehler beim Speichern')
    }
    setSaving(false)
  }

  const saveBranding = async () => {
    if (!org) return
    setSaving(true)
    try {
      await updateOrganization(org.id, {
        primary_color: primaryColor,
      } as any)
      await init()
      toast.success('Branding gespeichert')
      setStep(2)
    } catch (err) {
      console.error(err)
      toast.error('Fehler beim Speichern')
    }
    setSaving(false)
  }

  const startStripeOnboarding = async () => {
    setStripeLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Nicht angemeldet')
        setStripeLoading(false)
        return
      }

      const res = await fetch(`${SUPABASE_URL}/stripe-onboard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error('Stripe-Verbindung fehlgeschlagen')
      }
    } catch (err) {
      console.error(err)
      toast.error('Stripe-Verbindung fehlgeschlagen')
    }
    setStripeLoading(false)
  }

  const stripeConnected = !!(org as any)?.stripe_onboarding_complete

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i < step ? 'bg-emerald-600 text-white' :
                i === step ? 'bg-indigo-600 text-white' :
                'bg-slate-800 text-slate-500'
              }`}>
                {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 ${i < step ? 'bg-emerald-600' : 'bg-slate-800'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          {/* Step 0: Club-Info */}
          {step === 0 && (
            <div>
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-5">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Club-Informationen</h2>
              <p className="text-slate-400 text-sm mb-6">
                Grundlegende Infos zu deinem Club. Du kannst alles sp&auml;ter &auml;ndern.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">Club-Name</label>
                  <input
                    type="text"
                    value={clubName}
                    onChange={(e) => setClubName(e.target.value)}
                    placeholder="z.B. Club Paradiso"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">Stadt</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="z.B. K\u00f6ln"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">Kurzbeschreibung</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ein Satz \u00fcber euren Club..."
                    rows={2}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                  />
                </div>

                <button
                  onClick={saveClubInfo}
                  disabled={saving || !clubName.trim()}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Weiter
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Branding */}
          {step === 1 && (
            <div>
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-5">
                <Palette className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Dein Club-Look</h2>
              <p className="text-slate-400 text-sm mb-6">
                Logo und Prim&auml;rfarbe f&uuml;r dein Dashboard und die Club-Website.
              </p>

              <div className="space-y-5">
                {org && (
                  <LogoUpload orgId={org.id} currentLogoUrl={org.logo_url} />
                )}

                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">Prim&auml;rfarbe</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    />
                  </div>
                </div>

                <button
                  onClick={saveBranding}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Weiter
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <button onClick={() => setStep(2)} className="w-full text-sm text-slate-500 hover:text-white py-2 transition-colors">
                  Sp&auml;ter machen
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Stripe einrichten */}
          {step === 2 && (
            <div>
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-5">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Zahlungen einrichten</h2>
              <p className="text-slate-400 text-sm mb-6">
                Verbinde dein Stripe-Konto, um Online-Zahlungen zu akzeptieren. Die Einrichtung dauert ca. 5 Minuten.
              </p>

              <div className="space-y-4">
                {stripeConnected ? (
                  <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium text-sm">Stripe verbunden</p>
                      <p className="text-emerald-400/70 text-xs">Zahlungen sind einsatzbereit.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={startStripeOnboarding}
                      disabled={stripeLoading}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
                    >
                      {stripeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Mit Stripe verbinden
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setStep(3)}
                      className="w-full text-sm text-slate-500 hover:text-white py-2 transition-colors"
                    >
                      Sp&auml;ter einrichten
                    </button>

                    <div className="flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-400/80 leading-relaxed">
                        Ohne Stripe-Verbindung k&ouml;nnen keine Online-Zahlungen entgegengenommen werden. Du kannst Stripe jederzeit sp&auml;ter in den Einstellungen verbinden.
                      </p>
                    </div>
                  </>
                )}

                {stripeConnected && (
                  <button
                    onClick={() => setStep(3)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-colors"
                  >
                    Weiter
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Erstes Event */}
          {step === 3 && (
            <div>
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-5">
                <CalendarPlus className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Alles bereit!</h2>
              <p className="text-slate-400 text-sm mb-6">
                Dein Club-System ist eingerichtet. Erstelle jetzt dein erstes Event oder starte direkt im Dashboard.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/events/new')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-colors"
                >
                  Erstes Event erstellen
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full text-sm text-slate-500 hover:text-white py-2 transition-colors"
                >
                  Sp&auml;ter erstellen — zum Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
